import React, { useState } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import apiService from "../../services/api_services.js";
import auth_service from "../../services/auth_service.js";

const PrescriptionModal = ({ isOpen, onClose, onPrescriptionAdded }) => {
  const [hasTwoPD, setHasTwoPD] = useState(false);
  const [prescription, setPrescription] = useState({
    right: { sph: "0", cyl: "0", axis: "0", add: "0" },
    left: { sph: "0", cyl: "0", axis: "0", add: "0" },
    pd: "",
    rightPD: "",
    leftPD: "",
    name: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handlePrescriptionChange = (eye, field, value) => {
    setPrescription((prev) => ({
      ...prev,
      [eye]: {
        ...prev[eye],
        [field]: value,
      },
    }));
  };

  const handlePDChange = (value) => {
    setPrescription((prev) => ({
      ...prev,
      pd: value,
    }));
  };

  const handleSinglePDChange = (eye, value) => {
    setPrescription((prev) => ({
      ...prev,
      [`${eye}PD`]: value,
    }));
  };



  const handleNameChange = (value) => {
    setPrescription((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const customerId = auth_service.getAccessTokenData()?.id;
      
      if (!customerId) {
        toast.error("Please login to add prescription");
        return;
      }

      const payload = {
        customer_id: customerId,
        prescription_name: prescription.name || 'My Prescription',
        prescription: {
          right: {
            sph: prescription.right.sph || '0',
            cyl: prescription.right.cyl || '0',
            axis: prescription.right.axis || '0',
            add: prescription.right.add || '0'
          },
          left: {
            sph: prescription.left.sph || '0',
            cyl: prescription.left.cyl || '0',
            axis: prescription.left.axis || '0',
            add: prescription.left.add || '0'
          },
          pd: prescription.pd || '',
          rightPD: prescription.rightPD || '',
          leftPD: prescription.leftPD || '',
          hasTwoPD: hasTwoPD
        }
      };

      const response = await apiService.addPrescriptionJSON(payload);
      
      if (response.success) {
        toast.success("Prescription added successfully!", {
          duration: 3000,
          position: "top-center",
          style: {
            background: "#1F509A",
            color: "#fff",
          },
        });
        onPrescriptionAdded();
        onClose();
        resetForm();
      } else {
        toast.error(response.message || "Failed to add prescription");
      }
    } catch (error) {
      console.error("Error adding prescription:", error);
      toast.error("Failed to add prescription");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setHasTwoPD(false);
    setPrescription({
      right: { sph: "0", cyl: "0", axis: "0", add: "0" },
      left: { sph: "0", cyl: "0", axis: "0", add: "0" },
      pd: "",
      rightPD: "",
      leftPD: "",
      name: "",
    });
  };

  const isSubmitDisabled = !prescription.name.trim();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 text-white rounded-t-xl"
             style={{ background: 'linear-gradient(to right, #1F509A, #000066)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              Add New Prescription
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-blue-100 text-sm mt-2">Add your prescription for future orders</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Prescription Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Prescription Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={prescription.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g., My Reading Glasses, Distance Vision"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ focusRingColor: '#1F509A' }}
            />
          </div>

          {/* Prescription Input Form */}
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-2">
              <div className="text-center">
                <h3 className="text-base font-semibold text-gray-800 mb-2">
                  Enter Prescription Values
                </h3>
                <p className="text-sm text-gray-600">
                  Manually enter each prescription value for precise fitting
                </p>
              </div>
            </div>
            
            {/* Prescription Form */}
            <div className="space-y-6">
              {/* Right Eye Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-900 to-blue-800 rounded-full flex items-center justify-center text-white font-bold"
                       style={{ backgroundColor: '#1F509A' }}>
                    R
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Right Eye (OD)</h3>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SPH <span className="text-gray-400">(Sphere)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-900 bg-white text-center font-medium"
                      style={{ focusBorderColor: '#1F509A' }}
                      value={prescription.right.sph}
                      onChange={(e) => handlePrescriptionChange("right", "sph", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      CYL <span className="text-gray-400">(Cylinder)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-900 bg-white text-center font-medium"
                      style={{ focusBorderColor: '#1F509A' }}
                      value={prescription.right.cyl}
                      onChange={(e) => handlePrescriptionChange("right", "cyl", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Axis
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="180"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-900 bg-white text-center font-medium"
                      style={{ focusBorderColor: '#1F509A' }}
                      value={prescription.right.axis}
                      onChange={(e) => handlePrescriptionChange("right", "axis", e.target.value)}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ADD <span className="text-gray-400">(Addition)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-900 bg-white text-center font-medium"
                      style={{ focusBorderColor: '#1F509A' }}
                      value={prescription.right.add}
                      onChange={(e) => handlePrescriptionChange("right", "add", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Left Eye Section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white font-bold">
                    L
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Left Eye (OS)</h3>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      SPH <span className="text-gray-400">(Sphere)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-600 bg-white text-center font-medium"
                      value={prescription.left.sph}
                      onChange={(e) => handlePrescriptionChange("left", "sph", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      CYL <span className="text-gray-400">(Cylinder)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-600 bg-white text-center font-medium"
                      value={prescription.left.cyl}
                      onChange={(e) => handlePrescriptionChange("left", "cyl", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Axis
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="180"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-600 bg-white text-center font-medium"
                      value={prescription.left.axis}
                      onChange={(e) => handlePrescriptionChange("left", "axis", e.target.value)}
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ADD <span className="text-gray-400">(Addition)</span>
                    </label>
                    <input
                      type="number"
                      step="0.25"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-green-600 bg-white text-center font-medium"
                      value={prescription.left.add}
                      onChange={(e) => handlePrescriptionChange("left", "add", e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* PD Section */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-800">Pupillary Distance (PD)</h3>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={hasTwoPD}
                      onChange={(e) => setHasTwoPD(e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Separate PD values</span>
                  </label>
                </div>
                
                {!hasTwoPD ? (
                  <div className="max-w-xs">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      PD (mm)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 bg-white text-center font-medium"
                      value={prescription.pd}
                      onChange={(e) => handlePDChange(e.target.value)}
                      placeholder="62.0"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 max-w-md">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Right PD (mm)
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 bg-white text-center font-medium"
                        value={prescription.rightPD}
                        onChange={(e) => handleSinglePDChange("right", e.target.value)}
                        placeholder="31.0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Left PD (mm)
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 bg-white text-center font-medium"
                        value={prescription.leftPD}
                        onChange={(e) => handleSinglePDChange("left", e.target.value)}
                        placeholder="31.0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-6 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled || isSubmitting}
              className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                isSubmitDisabled || isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-900 to-blue-800 text-white hover:opacity-90'
              }`}
              style={{ 
                background: !isSubmitDisabled && !isSubmitting ? 'linear-gradient(to right, #1F509A, #000066)' : undefined 
              }}
            >
              {isSubmitting ? 'Adding...' : 'Add Prescription'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PrescriptionModal;